import { createFeatureSelector, createSelector } from '@ngrx/store';

import { AuthActions, AuthActionTypes } from '../actions/auth.actions';

import { CurrentUser } from 'modules/shared/models/account.models';
import { Role } from 'modules/shared/models/role.models';
import { LoginResponse, ForgotPasswordResponse } from 'modules/shared/models/auth.models';

export interface State {
  user: CurrentUser;
  roles: Array<Role>;
  loginChallengeRequired: LoginResponse;
  confirmNinetyDaysPassword: any;
  newPasswordRequired: LoginResponse;
  completeFirstLogin: any;
  updateUserName: boolean;
  forgotPassword: ForgotPasswordResponse;
  resetPassword: boolean;
  changePassword: boolean;
  enableMfa: string;
  mfaConfirm: boolean;
  mfaConfirmLogin: any;
  disableMfa: boolean;
  twoFAResend: any;
  loading: Array<AuthActionTypes>;
  errorMessage: string | null;
}

export const initialState: State = {
  user: null,
  roles: [],
  loginChallengeRequired: null,
  newPasswordRequired: null,
  completeFirstLogin: null,
  confirmNinetyDaysPassword: null,
  twoFAResend: null,
  updateUserName: null,
  forgotPassword: null,
  resetPassword: null,
  changePassword: null,
  enableMfa: null,
  mfaConfirm: null,
  mfaConfirmLogin: null,
  disableMfa: null,
  loading: [],
  errorMessage: null
};

export function Reducer(state: State = initialState, action: AuthActions): State {
  switch (action.type) {

    // Start Login
    case AuthActionTypes.LOGIN: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.LOGIN_SUCCESS: {
      return {
        ...state,
        user: action.payload.user,
        loading: clearLoading(state, AuthActionTypes.LOGIN),
        errorMessage: null
      };
    }

    case AuthActionTypes.LOGIN_FAILURE: {
      return {
        ...state,
        user: null,
        loading: clearLoading(state, AuthActionTypes.LOGIN),
        errorMessage: 'Incorrect username and/or password.'
      };
    }

    case AuthActionTypes.CLEAR_LOGIN: {
      return {
        ...state,
        user: null,
        loading: clearLoading(state, AuthActionTypes.LOGIN),
        errorMessage: null
      };
    }
    // End login

    // Start Get Current User
    case AuthActionTypes.GET_USER: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.GET_USER_SUCCESS: {
      return {
        ...state,
        user: action.payload,
        loading: clearLoading(state, AuthActionTypes.GET_USER),
        errorMessage: null
      };
    }

    case AuthActionTypes.GET_USER_FAILURE: {
      return {
        ...state,
        user: null,
        loading: clearLoading(state, AuthActionTypes.GET_USER),
        errorMessage: 'No user'
      };
    }

    case AuthActionTypes.CLEAR_GET_USER: {
      return {
        ...state,
        user: null,
        loading: clearLoading(state, AuthActionTypes.GET_USER),
        errorMessage: null
      };
    }
    // End Get Current User

    // Start Login Challenge Required
    case AuthActionTypes.LOGIN_CHALLENGE_REQUIRED: {
      return {
        ...state,
        loginChallengeRequired: action.payload,
        loading: clearLoading(state, AuthActionTypes.LOGIN),
        errorMessage: null
      };
    }

    case AuthActionTypes.CLEAR_LOGIN_CHALLENGE_REQUIRED: {
      return {
        ...state,
        loginChallengeRequired: null,
        loading: clearLoading(state, AuthActionTypes.LOGIN_CHALLENGE_REQUIRED),
        errorMessage: null
      };
    }
    // End Login Challenge Required

    // Start Confirm Ninety Days Password
    case AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD_SUCCESS: {
      return {
        ...state,
        confirmNinetyDaysPassword: action.payload,
        loading: clearLoading(state, AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD),
        errorMessage: null
      };
    }

    case AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD_FAILURE: {
      return {
        ...state,
        confirmNinetyDaysPassword: null,
        loading: clearLoading(state, AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD),
        errorMessage: 'Confirm ninety days password failed'
      };
    }

    case AuthActionTypes.CLEAR_CONFIRM_NINETY_DAYS_PASSWORD: {
      return {
        ...state,
        confirmNinetyDaysPassword: null,
        loading: clearLoading(state, AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD),
        errorMessage: null
      };
    }
    // End Confirm Ninety Days Password

    // Start Forgot Password
    case AuthActionTypes.FORGOT_PASSWORD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.FORGOT_PASSWORD_SUCCESS: {
      return {
        ...state,
        forgotPassword: action.payload,
        loading: clearLoading(state, AuthActionTypes.FORGOT_PASSWORD),
        errorMessage: null
      };
    }

    case AuthActionTypes.FORGOT_PASSWORD_FAILURE: {
      return {
        ...state,
        forgotPassword: null,
        loading: clearLoading(state, AuthActionTypes.FORGOT_PASSWORD),
        errorMessage: 'Forgot password failed'
      };
    }

    case AuthActionTypes.CLEAR_FORGOT_PASSWORD: {
      return {
        ...state,
        forgotPassword: null,
        loading: clearLoading(state, AuthActionTypes.FORGOT_PASSWORD),
        errorMessage: null
      };
    }
    // End Forgot Password

    // Start Complete First Login
    case AuthActionTypes.COMPLETE_FIRST_LOGIN: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.COMPLETE_FIRST_LOGIN_SUCCESS: {
      return {
        ...state,
        completeFirstLogin: action.payload,
        loading: clearLoading(state, AuthActionTypes.COMPLETE_FIRST_LOGIN),
        errorMessage: null
      };
    }

    case AuthActionTypes.COMPLETE_FIRST_LOGIN_FAILURE: {
      return {
        ...state,
        completeFirstLogin: null,
        loading: clearLoading(state, AuthActionTypes.COMPLETE_FIRST_LOGIN),
        errorMessage: 'Complete first login failed'
      };
    }

    case AuthActionTypes.CLEAR_COMPLETE_FIRST_LOGIN: {
      return {
        ...state,
        completeFirstLogin: null,
        loading: clearLoading(state, AuthActionTypes.COMPLETE_FIRST_LOGIN),
        errorMessage: null
      };
    }
    // End Complete First Login

    // Start Reset Password
    case AuthActionTypes.RESET_PASSWORD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.RESET_PASSWORD_SUCCESS: {
      return {
        ...state,
        resetPassword: action.payload,
        loading: clearLoading(state, AuthActionTypes.RESET_PASSWORD),
        errorMessage: null
      };
    }

    case AuthActionTypes.RESET_PASSWORD_FAILURE: {
      return {
        ...state,
        resetPassword: null,
        loading: clearLoading(state, AuthActionTypes.RESET_PASSWORD),
        errorMessage: 'Reset password failed'
      };
    }

    case AuthActionTypes.CLEAR_RESET_PASSWORD: {
      return {
        ...state,
        resetPassword: null,
        loading: clearLoading(state, AuthActionTypes.RESET_PASSWORD),
        errorMessage: null
      };
    }
    // End Reset Password

    // Start Change Password
    case AuthActionTypes.CHANGE_PASSWORD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.CHANGE_PASSWORD_SUCCESS: {
      return {
        ...state,
        changePassword: action.payload,
        loading: clearLoading(state, AuthActionTypes.CHANGE_PASSWORD),
        errorMessage: null
      };
    }

    case AuthActionTypes.CHANGE_PASSWORD_FAILURE: {
      return {
        ...state,
        changePassword: null,
        loading: clearLoading(state, AuthActionTypes.CHANGE_PASSWORD),
        errorMessage: 'Change password failed'
      };
    }

    case AuthActionTypes.CLEAR_CHANGE_PASSWORD: {
      return {
        ...state,
        changePassword: null,
        loading: clearLoading(state, AuthActionTypes.CHANGE_PASSWORD),
        errorMessage: null
      };
    }
    // End Change Password

    // Start Update User Name
    case AuthActionTypes.UPDATE_USER_NAME: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.UPDATE_USER_NAME_SUCCESS: {
      return {
        ...state,
        updateUserName: action.payload,
        loading: clearLoading(state, AuthActionTypes.UPDATE_USER_NAME),
        errorMessage: null
      };
    }

    case AuthActionTypes.UPDATE_USER_NAME_FAILURE: {
      return {
        ...state,
        updateUserName: null,
        loading: clearLoading(state, AuthActionTypes.UPDATE_USER_NAME),
        errorMessage: 'Update username failed'
      };
    }

    case AuthActionTypes.CLEAR_UPDATE_USER_NAME: {
      return {
        ...state,
        updateUserName: null,
        loading: clearLoading(state, AuthActionTypes.UPDATE_USER_NAME),
        errorMessage: null
      };
    }
    // End Update User Name

    // Start Enable Mfa
    case AuthActionTypes.ENABLE_MFA: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.ENABLE_MFA_SUCCESS: {
      return {
        ...state,
        enableMfa: action.payload,
        loading: clearLoading(state, AuthActionTypes.ENABLE_MFA),
        errorMessage: null
      };
    }

    case AuthActionTypes.ENABLE_MFA_FAILURE: {
      return {
        ...state,
        enableMfa: null,
        loading: clearLoading(state, AuthActionTypes.ENABLE_MFA),
        errorMessage: 'Enable Mfa failed'
      };
    }

    case AuthActionTypes.CLEAR_ENABLE_MFA: {
      return {
        ...state,
        enableMfa: null,
        loading: clearLoading(state, AuthActionTypes.ENABLE_MFA),
        errorMessage: null
      };
    }
    // End Enable Mfa

    // Start Mfa Confirm
    case AuthActionTypes.MFA_CONFIRM: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.MFA_CONFIRM_SUCCESS: {
      return {
        ...state,
        mfaConfirm: action.payload,
        loading: clearLoading(state, AuthActionTypes.MFA_CONFIRM),
        errorMessage: null
      };
    }

    case AuthActionTypes.MFA_CONFIRM_FAILURE: {
      return {
        ...state,
        mfaConfirm: null,
        loading: clearLoading(state, AuthActionTypes.MFA_CONFIRM),
        errorMessage: 'Mfa confirm failed'
      };
    }

    case AuthActionTypes.CLEAR_MFA_CONFIRM: {
      return {
        ...state,
        mfaConfirm: null,
        loading: clearLoading(state, AuthActionTypes.MFA_CONFIRM),
        errorMessage: null
      };
    }
    // End Mfa Confirm

    // Start Mfa Confirm Login
    case AuthActionTypes.MFA_CONFIRM_LOGIN: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.MFA_CONFIRM_LOGIN_SUCCESS: {
      return {
        ...state,
        mfaConfirmLogin: action.payload,
        loading: clearLoading(state, AuthActionTypes.MFA_CONFIRM_LOGIN),
        errorMessage: null
      };
    }

    case AuthActionTypes.MFA_CONFIRM_LOGIN_FAILURE: {
      return {
        ...state,
        mfaConfirmLogin: null,
        loading: clearLoading(state, AuthActionTypes.MFA_CONFIRM_LOGIN),
        errorMessage: 'Mfa confirm failed'
      };
    }

    case AuthActionTypes.CLEAR_MFA_CONFIRM_LOGIN: {
      return {
        ...state,
        mfaConfirmLogin: null,
        loading: clearLoading(state, AuthActionTypes.MFA_CONFIRM),
        errorMessage: null
      };
    }
    // End Mfa Confirm Login

    // Start Disable Mfa
    case AuthActionTypes.DISABLE_MFA: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.DISABLE_MFA_SUCCESS: {
      return {
        ...state,
        disableMfa: action.payload,
        loading: clearLoading(state, AuthActionTypes.DISABLE_MFA),
        errorMessage: null
      };
    }

    case AuthActionTypes.DISABLE_MFA_FAILURE: {
      return {
        ...state,
        disableMfa: null,
        loading: clearLoading(state, AuthActionTypes.DISABLE_MFA),
        errorMessage: 'Disable Mfa failed'
      };
    }

    case AuthActionTypes.CLEAR_DISABLE_MFA: {
      return {
        ...state,
        disableMfa: null,
        loading: clearLoading(state, AuthActionTypes.DISABLE_MFA),
        errorMessage: null
      };
    }
    // End Disable Mfa

    // Logout
    case AuthActionTypes.LOGOUT: {
      return {
        ...state,
        user: null,
        errorMessage: null
      };
    }

    // Start Get User Roles
    case AuthActionTypes.GET_USER_ROLES: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }

    case AuthActionTypes.GET_USER_ROLES_SUCCESS: {
      return {
        ...state,
        roles: action.payload,
        loading: clearLoading(state, AuthActionTypes.GET_USER_ROLES),
        errorMessage: null
      };
    }

    case AuthActionTypes.GET_USER_ROLES_FAILURE: {
      return {
        ...state,
        roles: null,
        loading: clearLoading(state, AuthActionTypes.GET_USER_ROLES),
        errorMessage: 'Get User Roles failed'
      };
    }

    case AuthActionTypes.CLEAR_GET_USER_ROLES: {
      return {
        ...state,
        roles: null,
        loading: clearLoading(state, AuthActionTypes.GET_USER_ROLES),
        errorMessage: null
      };
    }
    // End Get User Roles

    default: {
      return {
        ...state
      };
    }
  }
}

// `createFeatureSelector` is for selecting a "Type"
// When reuqesting whole feature state, the app can call this const directly
// Selectors
const getAuth = createFeatureSelector<State>('auth');
export const Selectors = {
  getAuth,
  loginChallengeRequired: createSelector(getAuth, (state) => state.loginChallengeRequired),
  newPasswordRequired: createSelector(getAuth, (state) => state.newPasswordRequired),
  completeFirstLogin: createSelector(getAuth, (state) => state.completeFirstLogin),
  confirmNinetyDaysPassword: createSelector(getAuth, (state) => state.confirmNinetyDaysPassword),
  twoFAResend: createSelector(getAuth, (state) => state.twoFAResend),
  updateUserName: createSelector(getAuth, (state) => state.updateUserName),
  forgotPassword: createSelector(getAuth, (state) => state.forgotPassword),
  resetPassword: createSelector(getAuth, (state) => state.resetPassword),
  changePassword: createSelector(getAuth, (state) => state.changePassword),
  enableMfa: createSelector(getAuth, (state) => state.enableMfa),
  mfaConfirm: createSelector(getAuth, (state) => state.mfaConfirm),
  mfaConfirmLogin: createSelector(getAuth, (state) => state.mfaConfirmLogin),
  disableMfa: createSelector(getAuth, (state) => state.disableMfa),
  getUser: createSelector(getAuth, (state) => state.user),
  getUserRoles: createSelector(getAuth, (state) => state.roles),
  getLoading: createSelector(getAuth, (state: State) => state.loading)
};

function clearLoading(authState: State, action: AuthActionTypes): Array<AuthActionTypes> {
  return authState.loading.filter((loadingMarker: AuthActionTypes) => loadingMarker !== action);
}
